import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import useAuth from '../hooks/useAuth';
import config from '../config';
import TopNavBar from '../components/layout/TopNavBar';
import Footer from '../components/layout/Footer';
import './LoginPage.css';

/**
 * 루미나 화면 층 신작 — 블랙 `pages/LoginPage.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 블록 10 `lg-` 로그인).
 * WHY: SPEC §2 — 캡차 설정(설정 시만 표시·실패 시 리셋)·오류 문구·로그인 중 상태·가입 이동은 원본 훅·핸들러가 그대로 담당하고,
 *      화면은 카드 폼 한 칸으로 그린다.
 *      App.js 가 /login 에서 헤더·푸터를 숨기므로(hideLayout) 상단 바·푸터는 이 화면 안에서 직접 그린다(계약 §7).
 * [2026-09-30 운영자 "로그인과 회원가입 등 페이지 디자인을 수정해줘"] 승인 시안(lumina-mockup.html #/login)대로 패널·카드를 채운다.
 *      종전에는 패널 카피·게임사 수 칩·캐릭터를 "시안 임의 요소"로 빼고(SPEC §3.4) 로고+사이트명만 두어 패널이 비었고,
 *      로고가 "MEMBER LOGIN" 알약 옆에 붙어 솟았으며, 카드 부제가 없어 제목과 첫 라벨이 4px 로 붙었다(09-28 대조).
 *      시안 요소는 전부 사이트 자료로 채운다 — 문자 캡차만 실제 캡차(Turnstile, 설정 시)로 둔다.
 * [2026-10-01 운영자 "이걸 그냥 없애줘"(왼쪽 패널 화면)] 왼쪽 네이비 패널을 통째로 없앴다 — 알약·큰 제목(첫 화면 제목)·설명·게임사 수 칩·
 *      첫 화면 카드 부채꼴(09-30 "이건 너무 어설픈거 아니니?" 뒤 캐릭터 한 장을 바꾼 것)과, 패널만 쓰던 게임사 목록 요청·카드 조립도 함께 지웠다.
 *      로그인 동작(캡차·오류 문구·로그인 중 상태·가입 이동·찾기 안내)은 그대로다.
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // CAPTCHA 관련 상태
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaSettings, setCaptchaSettings] = useState({
    user_enabled: false,
    site_key: ''
  });
  const turnstileRef = useRef(null);

  // CAPTCHA 설정 불러오기
  useEffect(() => {
    const fetchCaptchaSettings = async () => {
      try {
        // user-api의 public 엔드포인트 사용 (같은 /api 경로 사용)
        const apiUrl = config.API_URL;
        console.log('[CAPTCHA] Fetching settings from:', `${apiUrl}/system-settings/captcha-settings/public`);
        const response = await fetch(`${apiUrl}/system-settings/captcha-settings/public`);
        const result = await response.json();
        console.log('[CAPTCHA] Settings received:', result);

        if (result.success && result.data?.captcha) {
          const settings = {
            user_enabled: result.data.captcha.user_enabled || false,
            site_key: result.data.captcha.site_key || ''
          };
          console.log('[CAPTCHA] Applying settings:', settings);
          setCaptchaSettings(settings);
        }
      } catch (error) {
        console.error('CAPTCHA 설정 로드 실패:', error);
      }
    };
    fetchCaptchaSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 입력 시 에러 메시지 제거
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      setError('아이디와 비밀번호를 입력해주세요.');
      return;
    }

    // CAPTCHA 활성화 시 토큰 검증
    if (captchaSettings.user_enabled && captchaSettings.site_key) {
      if (!captchaToken) {
        setError('CAPTCHA 인증을 완료해주세요.');
        return;
      }
    }

    setLoading(true);
    try {
      const result = await login({
        username: formData.username,
        password: formData.password,
        captchaToken: captchaToken
      });
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || '로그인에 실패했습니다.');
        // CAPTCHA 리셋
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
        setCaptchaToken('');
      }
    } catch (err) {
      setError(err.message || '로그인에 실패했습니다.');
      // CAPTCHA 리셋
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setCaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = () => {
    navigate('/register');
  };

  /* 아이디/비밀번호 찾기 — 셀프 찾기 기능은 없다. 운영자가 정한 비밀번호 안내(블랙 마이페이지 "비밀번호 변경" → "상부에 문의해주세요", W56)와 같게 안내한다.
     WHY: 종전 연결(href="#")은 눌러도 아무 일이 없었다. 시안 문구("가입 시 등록한 이메일로 안내")는 이 사이트에 없는 기능이라 쓰지 않는다. */
  const handleFindAccount = (e) => {
    e.preventDefault();
    alert('아이디/비밀번호 찾기는 상부에 문의해주세요.');
  };

  return (
    <>
      <TopNavBar />
      <section className="page page-app page-login">
        <div className="container">
          <div className="lg-wrap">
            {/* 카드 폼(가운데 한 칸) — 시안: LOGIN · 로그인 · 안내 문장 */}
            <div className="lk-card lg-card">
              <span className="eyebrow">LOGIN</span>
              <h3>로그인</h3>
              <p className="lg-desc">아이디와 비밀번호를 입력해주세요.</p>
              <form className="lk-form" onSubmit={handleSubmit} noValidate>
                {error && (
                  <div className="lk-note danger" role="alert">
                    {error}
                  </div>
                )}
                <div className="lk-field">
                  <label className="lk-label" htmlFor="lgId">아이디</label>
                  <input
                    id="lgId"
                    type="text"
                    name="username"
                    placeholder="아이디를 입력하세요"
                    className="lk-input"
                    value={formData.username}
                    onChange={handleInputChange}
                    autoComplete="username"
                  />
                </div>
                <div className="lk-field">
                  <label className="lk-label" htmlFor="lgPw">비밀번호</label>
                  <input
                    id="lgPw"
                    type="password"
                    name="password"
                    placeholder="PASS"
                    className="lk-input"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="current-password"
                  />
                </div>
                {/* CAPTCHA (자동입력 방지) — 관리자 설정(user_enabled + site_key)일 때만 Turnstile 위젯 */}
                {captchaSettings.user_enabled && captchaSettings.site_key && (
                  <div className="lk-field">
                    <span className="lk-label">CAPTCHA (자동입력 방지)</span>
                    <div className="lg-captcha-wrap">
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={captchaSettings.site_key}
                        onSuccess={(token) => setCaptchaToken(token)}
                        onError={() => {
                          setCaptchaToken('');
                          console.error('CAPTCHA 검증 실패');
                        }}
                        onExpire={() => {
                          setCaptchaToken('');
                        }}
                        options={{
                          theme: 'light',
                          size: 'normal'
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="lg-btns">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    <span>{loading ? '로그인 중...' : '로그인'}</span>
                    <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleRegisterClick}
                  >
                    회원가입
                  </button>
                </div>
                <p className="lg-warn">※ 미성년자 이용 절대 불가!</p>
                <div className="lg-links">
                  <a href="#" className="find-account" onClick={handleFindAccount}>아이디/비밀번호 찾기</a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default LoginPage;
